import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { useApi } from "@/hooks/useApi";
import { useAuth } from "@/hooks/useAuth";
import { useEncryption } from "@/hooks/useEncryption";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { IconPicker } from "@/components/shared/IconPicker";
import { LoadingSpinner } from "@/components/shared/LoadingSpinner";
import { showToast } from "@/components/shared/Toast";
import { ArrowLeft, Save, Calendar } from "lucide-react";

interface SessionData {
  _key: string;
  name: string;
  icon: string;
  description: string;
  proposed_dates: string[];
  fixed_date: string | null;
  points: number | null;
}

export function AdminSessionEdit() {
  const { jwt, clientId, sessionId } = useParams();
  const navigate = useNavigate();
  const { getHeaders } = useAuth();
  const { encryptFields, decryptFields } = useEncryption(clientId);

  const { data: rawSession, loading, error } = useApi<SessionData>(
    `/api/admin/${jwt}/${clientId}/sessions/${sessionId}`,
  );

  const [name, setName] = useState("");
  const [icon, setIcon] = useState("📅");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);

  // Decrypt and populate fields once data arrives
  useEffect(() => {
    if (!rawSession) return;
    (async () => {
      const dec = await decryptFields(
        rawSession as unknown as Record<string, unknown>,
        ["name", "description"],
      );
      const s = dec as unknown as SessionData;
      setName(s.name || "");
      setIcon(s.icon || "📅");
      setDescription(s.description || "");
    })();
  }, [rawSession, decryptFields]);

  const handleSave = async () => {
    if (!name.trim()) {
      showToast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = await encryptFields(
        { name: name.trim(), icon, description },
        ["name", "description"],
      );
      const res = await fetch(
        `/api/admin/${jwt}/${clientId}/sessions/${sessionId}`,
        {
          method: "PATCH",
          headers: getHeaders(),
          body: JSON.stringify(payload),
        },
      );
      if (!res.ok) throw new Error((await res.json()).error ?? "Failed to save");
      showToast.success("Session updated!");
      navigate(`/admin/${jwt}/${clientId}/sessions/${sessionId}`);
    } catch (err) {
      showToast.error(err instanceof Error ? err.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <p className="text-app-danger text-body p-4">{error}</p>;
  if (!rawSession) return null;

  return (
    <div className="space-y-4 md:space-y-6 max-w-xl">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/admin/${jwt}/${clientId}/sessions/${sessionId}`)}
          className="rounded-xl hover:bg-app-surface-raised"
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-app-primary/15 flex items-center justify-center text-xl">
            {icon}
          </div>
          <div>
            <h1 className="text-heading font-bold leading-tight">Edit Session</h1>
            <p className="text-info text-app-text-muted">Update session details</p>
          </div>
        </div>
      </div>

      {/* Form */}
      <Card className="border-app-border rounded-2xl overflow-hidden pt-0">
        <CardHeader className="pb-2 bg-app-primary/5 rounded-t-2xl pt-4">
          <CardTitle className="text-body flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-app-primary/15 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-app-primary" />
            </div>
            <span>Session Info</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4 space-y-4">
          <div className="flex items-start gap-3">
            <div className="space-y-1">
              <Label>Icon</Label>
              <IconPicker value={icon} onSelect={setIcon} />
            </div>
            <div className="flex-1 space-y-1">
              <Label htmlFor="session-name">Name</Label>
              <Input
                id="session-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Session name"
                autoFocus
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="session-description">Description</Label>
            <Textarea
              id="session-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Optional description..."
              rows={4}
            />
          </div>

          <div className="flex gap-2 justify-end pt-1">
            <Button
              variant="outline"
              onClick={() =>
                navigate(`/admin/${jwt}/${clientId}/sessions/${sessionId}`)
              }
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-app-primary hover:bg-app-primary-hover"
            >
              <Save className="w-4 h-4 mr-1.5" />
              {saving ? "Saving…" : "Save Changes"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
